import { useMutation, UseMutationOptions, useQuery, useQueryClient } from 'react-query';

import { getRequest, postRequest, putRequest } from 'services/apiRequests';
import { CSRFService } from 'services/csrf';
import type { StringifiedUUID } from 'types';
import {
  AccountDealerApplyResponse,
  AccountDealerAsyncResponse,
  AccountDealerCustomOrderConfigurationPayload,
  AccountDealerPayload,
  AccountDealerRunRulesPayload,
  AccountDealerRunRulesResponse,
  AccountDealerStatusResponse,
} from 'types/accountDealer';
import { APIError } from 'utils/errors';
import { QueryHookOptions } from 'types/ReactQuery';

type AccountDealerAsyncRequest = {
  applyImmediately: boolean;
  payload: AccountDealerPayload;
};

type AccountDealerSaveRequest = {
  payload: AccountDealerPayload;
};

type AccountDealerCustomOrderRequest = {
  payload: AccountDealerRunRulesPayload;
};

const asyncApiUrl = (modelId: StringifiedUUID, applyImmediately: boolean) =>
  `/api/v1/tq/hierarchy-models/${modelId}/account-dealer-async/?apply_immediately=${applyImmediately}`;

const saveApiUrl = (modelId: StringifiedUUID) => `/api/v1/tq/hierarchy-models/${modelId}/account-dealer-save/`;

const customOrderApiUrl = (modelId: StringifiedUUID) =>
  `/api/v1/tq/hierarchy-models/${modelId}/account-dealer-custom-order`;

const postAccountDealerAsync = (modelId: StringifiedUUID, csrfToken: string) => (values: AccountDealerAsyncRequest) => {
  return postRequest<AccountDealerPayload, AccountDealerAsyncResponse>(
    asyncApiUrl(modelId, values.applyImmediately),
    values.payload,
    csrfToken,
  );
};

const useAccountDealerAsyncMutation = (
  modelId: StringifiedUUID,
  options: UseMutationOptions<AccountDealerAsyncResponse, APIError, AccountDealerAsyncRequest> = {},
) => {
  const { query: csrfQuery } = CSRFService.useGetCSRFToken();
  const csrfToken = csrfQuery.data?.csrf ?? '';

  return useMutation<AccountDealerAsyncResponse, APIError, AccountDealerAsyncRequest>({
    ...options,
    mutationFn: postAccountDealerAsync(modelId, csrfToken),
  });
};

const postAccountDealerSave = (modelId: StringifiedUUID, csrfToken: string) => (values: AccountDealerSaveRequest) => {
  return postRequest<AccountDealerPayload, AccountDealerAsyncResponse>(saveApiUrl(modelId), values.payload, csrfToken);
};

const useAccountDealerSaveMutation = (
  modelId: StringifiedUUID,
  options: UseMutationOptions<AccountDealerAsyncResponse, APIError, AccountDealerSaveRequest> = {},
) => {
  const { query: csrfQuery } = CSRFService.useGetCSRFToken();
  const csrfToken = csrfQuery.data?.csrf ?? '';

  return useMutation<AccountDealerAsyncResponse, APIError, AccountDealerSaveRequest>({
    ...options,
    mutationFn: postAccountDealerSave(modelId, csrfToken),
  });
};

const postAccountDealerCustomOrder =
  (modelId: StringifiedUUID, csrfToken: string) => (values: AccountDealerCustomOrderRequest) => {
    return postRequest<AccountDealerRunRulesPayload, AccountDealerRunRulesResponse>(
      `${customOrderApiUrl(modelId)}/run/`,
      values.payload,
      csrfToken,
    );
  };

const useAccountDealerCustomOrderMutation = (
  modelId: StringifiedUUID,
  options: UseMutationOptions<AccountDealerRunRulesResponse, APIError, AccountDealerCustomOrderRequest> = {},
) => {
  const { query: csrfQuery } = CSRFService.useGetCSRFToken();
  const csrfToken = csrfQuery.data?.csrf ?? '';

  return useMutation<AccountDealerRunRulesResponse, APIError, AccountDealerCustomOrderRequest>({
    ...options,
    mutationFn: postAccountDealerCustomOrder(modelId, csrfToken),
  });
};

const putAccountDealerCustomOrderConfig =
  (modelId: StringifiedUUID, csrfToken: string) => (values: AccountDealerCustomOrderRequest) => {
    return putRequest<AccountDealerRunRulesPayload, AccountDealerRunRulesResponse>(
      `${customOrderApiUrl(modelId)}/update-config/`,
      values.payload,
      csrfToken,
    );
  };

const usePutAccountDealerCustomOrderMutation = (
  modelId: StringifiedUUID,
  options: UseMutationOptions<AccountDealerRunRulesResponse, APIError, AccountDealerCustomOrderRequest> = {},
) => {
  const { query: csrfQuery } = CSRFService.useGetCSRFToken();
  const csrfToken = csrfQuery.data?.csrf ?? '';

  return useMutation<AccountDealerRunRulesResponse, APIError, AccountDealerCustomOrderRequest>({
    ...options,
    mutationFn: putAccountDealerCustomOrderConfig(modelId, csrfToken),
  });
};

const getAccountDealerCustomOrderConfiguration = (modelId: StringifiedUUID) =>
  getRequest<null, AccountDealerCustomOrderConfigurationPayload>(`${customOrderApiUrl(modelId)}/get-config/`);

const useGetAccountDealerCustomOrderConfiguration = (
  // eslint-disable-next-line @typescript-eslint/default-param-last
  options: QueryHookOptions<AccountDealerCustomOrderConfigurationPayload, APIError> = {},
  modelId: StringifiedUUID,
) => {
  const queryClient = useQueryClient();
  const QUERY_KEY = ['hierarchy-models', modelId, 'account-dealer-custom-order-configuration'];
  const query = useQuery<AccountDealerCustomOrderConfigurationPayload, APIError>({
    ...options,
    queryKey: QUERY_KEY,
    queryFn: () => getAccountDealerCustomOrderConfiguration(modelId),
  });

  const invalidateQuery = () => queryClient.invalidateQueries({ queryKey: QUERY_KEY });

  return {
    query,
    invalidateQuery,
  };
};

const applyApiUrl = (modelId: StringifiedUUID) => `/api/v1/tq/hierarchy-models/${modelId}/account-dealer-apply/`;

const postAccountDealerApply = (modelId: StringifiedUUID, csrfToken: string) => () => {
  return postRequest<null, AccountDealerApplyResponse>(applyApiUrl(modelId), null, csrfToken);
};

const useAccountDealerApplyMutation = (
  modelId: StringifiedUUID,
  options: UseMutationOptions<AccountDealerApplyResponse, APIError> = {},
) => {
  const queryClient = useQueryClient();
  const { query: csrfQuery } = CSRFService.useGetCSRFToken();
  const csrfToken = csrfQuery.data?.csrf ?? '';

  return useMutation<AccountDealerApplyResponse, APIError>({
    ...options,
    onSuccess: (data, variables, context) => {
      options.onSuccess?.(data, variables, context);
      // Clear dealer status query
      queryClient.removeQueries({ queryKey: ['hierarchy-models', modelId, 'account-dealer-status'] });
    },
    mutationFn: postAccountDealerApply(modelId, csrfToken),
  });
};

const discardApiUrl = (modelId: StringifiedUUID) => `/api/v1/tq/hierarchy-models/${modelId}/account-dealer-discard/`;

const postAccountDealerDiscard = (modelId: StringifiedUUID, csrfToken: string) => () => {
  return postRequest<null, null>(discardApiUrl(modelId), null, csrfToken);
};

const useAccountDealerDiscardMutation = (
  modelId: StringifiedUUID,
  options: UseMutationOptions<null, APIError> = {},
) => {
  const queryClient = useQueryClient();
  const { query: csrfQuery } = CSRFService.useGetCSRFToken();
  const csrfToken = csrfQuery.data?.csrf ?? '';

  return useMutation<null, APIError>({
    ...options,
    onSuccess: (data, variables, context) => {
      options.onSuccess?.(data, variables, context);
      // Clear dealer status query
      queryClient.removeQueries({ queryKey: ['hierarchy-models', modelId, 'account-dealer-status'] });
    },
    mutationFn: postAccountDealerDiscard(modelId, csrfToken),
  });
};

const hasDraftApiUrl = (modelId: StringifiedUUID) => `/api/v1/tq/hierarchy-models/${modelId}/account-dealer-status/`;

const getAccountDealerStatus = (modelId: StringifiedUUID) =>
  getRequest<null, AccountDealerStatusResponse>(hasDraftApiUrl(modelId));

const useGetAccountDealerStatusQuery = (
  modelId: StringifiedUUID,
  options: QueryHookOptions<AccountDealerStatusResponse, APIError> = {},
) => {
  const QUERY_KEY = ['hierarchy-models', modelId, 'account-dealer-status'];
  return useQuery<AccountDealerStatusResponse, APIError>({
    ...options,
    queryKey: QUERY_KEY,
    queryFn: () => getAccountDealerStatus(modelId),
  });
};

const getApiUrl = (modelId: StringifiedUUID, dataObjectId: StringifiedUUID) =>
  `/api/v1/tq/hierarchy-models/${modelId}/objects/${dataObjectId}/account-dealer-configuration/`;

const getAccountDealerConfiguration = (modelId: StringifiedUUID, territoryId: StringifiedUUID) =>
  getRequest<null, AccountDealerPayload>(getApiUrl(modelId, territoryId));

const useGetAccountDealerConfiguration = (
  // eslint-disable-next-line @typescript-eslint/default-param-last
  options: QueryHookOptions<AccountDealerPayload, APIError> = {},
  modelId: StringifiedUUID,
  dataObjectId: StringifiedUUID,
) => {
  const queryClient = useQueryClient();
  const QUERY_KEY = ['hierarchy-models', modelId, 'account-dealer-configuration', dataObjectId];
  const query = useQuery<AccountDealerPayload, APIError>({
    ...options,
    queryKey: QUERY_KEY,
    queryFn: () => getAccountDealerConfiguration(modelId, dataObjectId),
  });

  const invalidateQuery = () => queryClient.invalidateQueries({ queryKey: QUERY_KEY });

  return {
    query,
    invalidateQuery,
  };
};

export const AccountDealerService = {
  useAccountDealerAsyncMutation,
  useAccountDealerApplyMutation,
  useAccountDealerDiscardMutation,
  useGetAccountDealerStatusQuery,
  useGetAccountDealerConfiguration,
  useAccountDealerSaveMutation,
  useAccountDealerCustomOrderMutation,
  useGetAccountDealerCustomOrderConfiguration,
  usePutAccountDealerCustomOrderMutation,
};
